import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from '@dotone/react-ui-core/hooks';
import { useSearchSelector, useTranslate } from '@hooks';
import { searchOffers } from '@actions';
import { Form, Col, FormGroup as BsFormGroup } from 'reactstrap';
import {
  CountryRegionSelect,
  Label,
  FormAction,
  Button,
  Input,
  Alert,
  Spinner,
} from '@dotone/react-ui-core';
import { CategoryGroupSelect, MarketplaceMediaCategorySelect, OfferSearchInput } from '../forms';

const FormGroup = ({ label, required = true, children }) => (
  <BsFormGroup row>
    <Label xs={12} sm={4} md={3} required={required} className="text-left text-sm-right">
      {label}
    </Label>
    <Col xs={12} sm={8} md={9}>
      {children}
    </Col>
  </BsFormGroup>
);

const blankValues = {
  offerId: '',
  regions: [],
  categoryGroupIds: [],
  mediaCategoryIds: [],
  excludeMyAffiliates: false,
  eligibleForInvites: false,
};

const MarketplaceForm = ({
  defaultValues: givenDefaultValues,
  onSubmit,
  onReset,
  submitting = false,
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const { t, locale } = useTranslate('marketplace', 'form');

  const { connectForm, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: blankValues,
  });
  const [regions, categoryGroupIds] = watch(['regions', 'categoryGroupIds']);

  const { data: offers, status } = useSearchSelector(({ offerSearch }) => offerSearch, {
    activePublic: true,
    locale,
  });

  const handleReset = () => {
    reset(blankValues);
    onReset(blankValues);
  };

  useEffect(() => {
    if (offers.length === 1) {
      setValue('offerId', offers[0].id);
    }
  }, [offers, setValue]);

  useEffect(() => {
    dispatch(searchOffers({ activePublic: true, locale }));
  }, [dispatch, locale]);

  useEffect(() => {
    setValue('mediaCategoryIds', []);
  }, [setValue, categoryGroupIds, regions]);

  if (status !== 'success') {
    return <Spinner />;
  }

  if (!offers.length) {
    return (
      <Alert variant="warning">
        <p className="text-warning m-0">{t('notEligibleOffer')}</p>
      </Alert>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} {...otherProps}>
      {connectForm(
        <>
          <FormGroup label={t('common:model.offer')}>
            <OfferSearchInput
              name="offerId"
              loadInitial
              searchParams={{ activePublic: true, locale }}
              required
            />
          </FormGroup>
          <FormGroup label={t('region')}>
            <CountryRegionSelect multi name="regions" rules={{ required: true }} />
          </FormGroup>
          <FormGroup label={t('categoryGroups')}>
            <CategoryGroupSelect
              multi
              name="categoryGroupIds"
              rules={{ maxLength: 6, required: true }}
              placeholder={t('placeholder.categoryGroups')}
            />
          </FormGroup>
          {!!regions?.length && !!categoryGroupIds?.length && (
            <FormGroup label={t('mediaCategories')} required={false}>
              <MarketplaceMediaCategorySelect
                multi
                name="mediaCategoryIds"
                regions={regions}
                categoryGroupIds={categoryGroupIds}
                rules={{ maxLength: 6 }}
                placeholder={t('placeholder.mediaCategories')}
              />
            </FormGroup>
          )}
          <FormGroup required={false}>
            <Input type="switch" label={t('excludeMyAffiliates')} name="excludeMyAffiliates" />
            <Input type="switch" label={t('eligibleForInvites')} name="eligibleForInvites" />
          </FormGroup>
        </>
      )}
      <FormAction align="right">
        <Button type="submit" color="primary" submitting={submitting}>
          {t('common:actions.applyFilters')}
        </Button>
        <Button type="button" color="secondary" onClick={handleReset} submitting={submitting}>
          {t('common:actions.reset')}
        </Button>
      </FormAction>
    </Form>
  );
};

export default MarketplaceForm;
